// src/pages/guides/PHP2ShellGuide.js
// PHP2Shell (CVE-2026-109618 / CVE-2026-109619) advisory guide - PRD-198 WP2

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

const GUIDE_PATH = '/guides/php2shell-cve-2026-109618-109619';

function CodeBlock({ title, lines }) {
  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-block-title">{title}</span>
      </div>
      <pre>{lines.join('\n')}</pre>
    </div>
  );
}

function PHP2ShellGuide() {
  const startTrial = useTrialCta('guide_trial:php2shell');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "PHP2Shell (CVE-2026-109618 / CVE-2026-109619): Check and Patch Your PHP Servers",
    "description": "PHP2Shell is two unauthenticated remote code execution flaws in the PHP engine, fixed in PHP 8.5.12, 8.4.27, 8.3.36 and 8.2.35. How to check your version, patch, and what to do if you can't patch today.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-10-21T00:00:00-04:00",
    "dateModified": "2026-10-21T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": `https://www.driftalarm.com${GUIDE_PATH}`,
    "mainEntityOfPage": `https://www.driftalarm.com${GUIDE_PATH}`
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "PHP2Shell (CVE-2026-109618 / CVE-2026-109619)" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="PHP2Shell CVE-2026-109618 & CVE-2026-109619: Check and Patch PHP | DriftAlarm"
        description="PHP2Shell is two unauthenticated RCE flaws in PHP, fixed in 8.5.12, 8.4.27, 8.3.36 and 8.2.35. Check your PHP version, patch and restart, and contain servers you can't patch today."
        path={GUIDE_PATH}
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema(GUIDE_PATH)].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>PHP2Shell (CVE-2026-109618 / CVE-2026-109619): Check and Patch Your PHP Servers</h1>
          <GuideByline path={GUIDE_PATH} />
          <GuideAnswer path={GUIDE_PATH} />
          <p className="guide-hero-intro">
            On October 21, 2026 the PHP project shipped fixes for two critical flaws in the PHP
            engine itself. Neither needs a login, and neither depends on a bug in your application
            code. If a server runs PHP and answers web requests, it is in scope until it is patched
            and restarted.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Which PHP versions are affected and which release fixes each branch</li>
              <li>How to check your PHP version from outside and on the server</li>
              <li>Why a distribution package can be patched while reporting an older version</li>
              <li>What to do today if a server can't be patched yet</li>
            </ul>
          </div>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
            <Link to="/guides/eol-software" className="da-btn da-btn--secondary">
              EOL Software Playbook
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#what-it-is">What PHP2Shell Is</a></li>
            <li><a href="#affected">Affected and Fixed Versions</a></li>
            <li><a href="#check">How to Check Your PHP Version</a></li>
            <li><a href="#patch">Patch and Restart</a></li>
            <li><a href="#interim">If You Can't Patch Today</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="what-it-is" className="guide-section">
          <h2>What PHP2Shell Is</h2>

          <div className="callout callout-critical">
            <div className="callout-title">Unauthenticated Remote Code Execution in the PHP Engine</div>
            <div className="callout-content">
              <p>
                <strong>Both flaws are rated 9.2 (CVSS v4.0) and allow code to run with the
                privileges of the web server's PHP worker.</strong> They sit in PHP's own request
                handling, so keeping your application and plugins up to date does not close them.
              </p>
            </div>
          </div>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>CVE</th>
                  <th>Flaw</th>
                  <th>What It Allows</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>CVE-2026-109618</td>
                  <td>Memory corruption when a request hits the memory limit mid-allocation</td>
                  <td>Remote code execution. On Apache with mod_php it can be triggered before any application code runs.</td>
                </tr>
                <tr>
                  <td>CVE-2026-109619</td>
                  <td>Use-after-free: state from one request survives into the next request on the same worker</td>
                  <td>Remote code execution, worker crashes, and possible leakage of memory from earlier requests</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Every way of serving PHP on the web is in scope: Apache mod_php, PHP-FPM behind nginx
            or Apache, php-cgi, FrankenPHP and LiteSpeed. mod_php is the highest-risk setup because
            the attack path needs nothing from your application.
          </p>

          <div className="callout callout-warning">
            <div className="callout-title">Status as of October 21, 2026</div>
            <div className="callout-content">
              <ul>
                <li>The CVE IDs leaked on October 20, ahead of the scheduled release. Fixes shipped October 21.</li>
                <li>CISA has urged immediate patching and has not reported confirmed exploitation.</li>
                <li>Attack attempts against the flaws have been reported, and independent reproduction is under way. Treat a public exploit as imminent.</li>
                <li>Not yet listed in the CISA Known Exploited Vulnerabilities catalog.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="affected" className="guide-section">
          <h2>Affected and Fixed Versions</h2>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>PHP Branch</th>
                  <th>Vulnerable</th>
                  <th>Fixed In</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>8.5</td><td>Below 8.5.12</td><td>8.5.12</td></tr>
                <tr><td>8.4</td><td>Below 8.4.27</td><td>8.4.27</td></tr>
                <tr><td>8.3</td><td>Below 8.3.36</td><td>8.3.36</td></tr>
                <tr><td>8.2</td><td>Below 8.2.35</td><td>8.2.35</td></tr>
                <tr><td>8.1 and older</td><td>All releases</td><td>No routine fix. Upgrade to a supported branch.</td></tr>
              </tbody>
            </table>
          </div>
          <p>
            PHP 8.1 reached end of life on December 31, 2025. Limited source-level backports exist
            for some older branches, but no packaged release. For anything
            older than 8.2 the practical answer is the one in
            the <Link to="/guides/eol-software">EOL software playbook</Link>: upgrade, or take the
            system off the internet.
          </p>
        </section>

        <section id="check" className="guide-section">
          <h2>How to Check Your PHP Version</h2>

          <h3>From Outside</h3>
          <p>
            Many servers announce their PHP version in the <code>X-Powered-By</code> response header.
            This is what an attacker's scanner reads first.
          </p>
          <CodeBlock
            title="Check the version your site advertises"
            lines={[
              'curl -sI https://www.example.com | grep -i x-powered-by',
              '# X-Powered-By: PHP/8.3.12   <- below 8.3.36, vulnerable',
            ]}
          />
          <p>
            No header doesn't mean you're safe. It means the version is hidden, so confirm it on
            the host.
          </p>

          <h3>On the Server</h3>
          <p>
            Check the binary that actually serves web traffic. The command-line <code>php</code> and
            the PHP-FPM service are often different builds, and a server can run several PHP-FPM
            versions side by side.
          </p>
          <CodeBlock
            title="Web-serving PHP, not just the CLI"
            lines={[
              'php -v                       # CLI only',
              'php-fpm8.3 -v                # Debian/Ubuntu FPM (one per installed version)',
              'php-fpm -v                   # RHEL/Fedora FPM',
              'ls /etc/php/                 # every PHP version installed (Debian/Ubuntu)',
            ]}
          />

          <h3>Distribution Packages</h3>
          <div className="callout callout-info">
            <div className="callout-title">A Backported Fix Can Report an Older Version</div>
            <div className="callout-content">
              <p>
                Debian, Ubuntu, RHEL and their derivatives often patch a security flaw into the
                version they already ship rather than moving to the new upstream release. The server
                is fixed, but it still reports something like <code>8.2.29</code>. Check the
                package changelog for the CVE ID before concluding either way.
              </p>
            </div>
          </div>
          <CodeBlock
            title="Is the fix in your package?"
            lines={[
              '# Debian / Ubuntu',
              'apt changelog php8.3-fpm | grep -E "CVE-2026-10961[89]"',
              '',
              '# RHEL / Alma / Rocky / Fedora',
              'rpm -q --changelog php-fpm | grep -E "CVE-2026-10961[89]"',
            ]}
          />
        </section>

        <section id="patch" className="guide-section">
          <h2>Patch and Restart</h2>
          <ol>
            <li><strong>Update PHP</strong> from your distribution's security repository, or to the upstream fixed release for your branch.</li>
            <li><strong>Restart every process that loaded PHP.</strong> A running PHP-FPM pool or Apache with mod_php keeps the old, vulnerable code in memory until it restarts.</li>
            <li><strong>Re-check the version</strong> from the server and from outside, using the commands above.</li>
            <li><strong>Check every host.</strong> Staging servers, old marketing sites and admin tools on separate subdomains run PHP too.</li>
          </ol>
          <CodeBlock
            title="Restart after updating (adjust the version)"
            lines={[
              'sudo systemctl restart php8.3-fpm     # PHP-FPM',
              'sudo systemctl restart apache2        # Apache + mod_php (httpd on RHEL)',
            ]}
          />
          <div className="callout callout-warning">
            <div className="callout-title">Check Where Your PHP Comes From</div>
            <div className="callout-content">
              <p>
                Some third-party package repositories and hosting control panels that compile PHP
                themselves did not have fixed builds on release day. If your PHP comes from one of
                them, confirm a fixed build exists before assuming the update went through.
              </p>
            </div>
          </div>
        </section>

        <section id="interim" className="guide-section">
          <h2>If You Can't Patch Today</h2>
          <p>
            CISA's guidance is direct: if a PHP system can't be updated right away, or still runs
            PHP 8.1 or older, take it offline until it is. If that isn't possible, these steps
            narrow the window. None of them is a fix.
          </p>
          <ul>
            <li><strong>Restrict reach:</strong> limit the site to known IP ranges or put it behind a VPN while you patch.</li>
            <li><strong>WAF rules:</strong> a web application firewall may block known exploit patterns. Check that its rules have been updated for PHP2Shell.</li>
            <li><strong>Upload progress tracking:</strong> setting <code>session.upload_progress.enabled = Off</code> in php.ini has been reported to reduce exposure for some attack paths. Test it first, then restart PHP.</li>
            <li><strong>Watch the workers:</strong> alert on PHP or web-server processes that spawn shells, run download tools, or open outbound connections they normally wouldn't.</li>
          </ul>
          <div className="callout callout-info">
            <div className="callout-title">Hiding the Version Is Not a Fix</div>
            <div className="callout-content">
              <p>
                <code>expose_php = Off</code> removes the <code>X-Powered-By</code> header. Removing
                easy fingerprints is good practice, but automated attacks often skip the version
                check and try the exploit anyway.
              </p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>How DriftAlarm Helps</h3>
            <p>
              DriftAlarm's external scans fingerprint the PHP version each of your hosts advertises.
              Any version below the PHP2Shell fix, and any end-of-life branch, is flagged as a
              critical finding with the fixed release named, and an alarm fires when a vulnerable
              version appears on your perimeter. Hosts that hide their version can't be confirmed
              from outside, so use the server-side checks above for those.
            </p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <ul>
            <li>
              <Link to="/guides/eol-software">EOL Software Playbook</Link>: what to do with PHP 8.1 and older, now that no routine fix is coming.
            </li>
            <li>
              <Link to="/guides/patch-window-collapse">The Patch Window Is Dead</Link>: why the gap between disclosure and exploitation keeps shrinking.
            </li>
            <li>
              <Link to="/guides/waf-and-vulnerability-scanning">WAF and Vulnerability Scanning</Link>: what a WAF does and doesn't hide while a fix is pending.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring</Link>: finding the PHP hosts nobody remembered were public.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary: PHP2Shell Response Checklist</h2>
          <div className="quickstart-list">
            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">Today</span>
                <span className="quickstart-day-title">Find and Fix</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>List every internet-facing host that runs PHP, including staging and forgotten subdomains</li>
                <li>Check the web-serving PHP version and the package changelog on each</li>
                <li>Update to 8.5.12, 8.4.27, 8.3.36 or 8.2.35 (or the distribution's patched package)</li>
                <li>Restart PHP-FPM and Apache, then re-check the version</li>
              </ul>
            </div>

            <div className="quickstart-day">
              <div className="quickstart-day-header">
                <span className="quickstart-day-label">This Week</span>
                <span className="quickstart-day-title">Close the Stragglers</span>
              </div>
              <ul className="quickstart-day-tasks">
                <li>Take PHP 8.1 and older hosts offline or restrict them until upgraded</li>
                <li>Review web and PHP-FPM logs for crashes and unusual child processes since October 20</li>
                <li>Set <code>expose_php = Off</code> on hosts that still advertise their version</li>
                <li>Put the internet-facing PHP inventory on a recurring check, not a one-off sweep</li>
              </ul>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>→</span>
            </button>
          </div>
        </section>
        <GuideFAQ path={GUIDE_PATH} />
      </main>

      <PublicFooter />
    </div>
  );
}

export default PHP2ShellGuide;
